import { Box, Button, FormControl, FormLabel, Input, Switch, useToast } from "@chakra-ui/react";
import LocationAutocomplete from "components/elements/LocationAutocomplete";
import PageHeader from "components/layout/PageHeader";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";
import StepDescription from "../StepDescription";
import StepTitle from "../StepTitle";
import { useCurrentUserQuery } from "modules/tenant/queries/use-current-user-query";

const CompanyAddress = () => {
  const { user } = useTenant();
  const { refetch } = useCurrentUserQuery();
  const navigate = useNavigate();
  const toast = useToast();
  const company = user.company || {};
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState<any>(company.address);
  const [mailingAddress, setMailingAddress] = useState<any>(company.mailing_address);
  const [sameMailing, setSameMailing] = useState(true);
  const [companyName, setCompanyName] = useState(company.name || "");

  const updateCompany = async () => {
    // Validation
    if (!companyName.trim()) {
      toast({
        title: "Company Name Required",
        description: "Please enter a company name.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!address || !address.formatted) {
      toast({
        title: "Address Required",
        description: "Please select a company address.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      await apiRequest("/supabase/companies/update", {
        values: {
          name: companyName,
          address,
          mailing_address: sameMailing ? address : mailingAddress,
        },
      });
      await refetch();
      
      toast({
        title: "Company Details Updated",
        description: "Your company information has been saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Navigate to the next step
      navigate("/setup/invite");
    } catch (error) {
      console.error(error);
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update company details. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      user.company.address &&
      user.company.mailing_address &&
      user.company.address.formatted !== user.company.mailing_address.formatted
    ) {
      setSameMailing(false);
    }
  }, [user]);

  return (
    <Box py="34px" px="26px" flex="1">
      <PageHeader title="Add Company Details" hideUser />

      <Box>
        <StepTitle>Company Location</StepTitle>
        <StepDescription>Add the address for your business.</StepDescription>
        <FormControl maxW="480px" mb="24px">
          <FormLabel>Company Name</FormLabel>
          <Input
            value={companyName}
            onChange={ev => setCompanyName(ev.target.value)}
            // isInvalid={invalidFields.includes("fundingDate")}
            placeholder="West Coast Builds"
            variant="primary"
          />
        </FormControl>

        <FormControl maxW="480px" mb="24px">
          <FormLabel>Address</FormLabel>
          <LocationAutocomplete onChange={setAddress} defaultValue={(address || {}).formatted} />
        </FormControl>
      </Box>

      <FormLabel>Mailing Address</FormLabel>
      <FormControl display="flex" alignItems="center" mb="24px">
        <Switch
          isChecked={sameMailing}
          id="same-mailing"
          onChange={ev => setSameMailing(ev.target.checked)}
        />
        <FormLabel htmlFor="same-mailing" fontWeight="500" fontSize="12px" ml="20px" mb="0px">
          Same as Company Address
        </FormLabel>
      </FormControl>

      {!sameMailing && (
        <FormControl maxW="480px" mb="24px">
          <LocationAutocomplete
            onChange={setMailingAddress}
            defaultValue={(mailingAddress || {}).formatted}
          />
        </FormControl>
      )}

      <Button 
        size="sm" 
        mr="16px" 
        isLoading={isLoading} 
        onClick={updateCompany}
        colorScheme="blue"
        isDisabled={!companyName.trim() || !address?.formatted}
      >
        Continue
      </Button>
    </Box>
  );
};

export default CompanyAddress;
